import { SaveButton } from '@/components/_common';
import {
  AccountPayableGenerateGroupingFormValues,
  InstallmentFromGroupingsList,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { Button, Form, Modal, ModalProps, Space, notification } from 'antd';
import GenerateGroupingForm from './GenerateGroupingForm';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { MathRound } from '@/utils/functions/MathRound';
import GenerateGroupingInstallmentList from './GenerateGroupingInstallmentList';
import { useAccountPayableGenerateGrouping } from '@/core/domain/financial/AccountPayable/AccountPayable.hooks';
import { DateTimeFormat } from '@/utils/moment';

interface Props extends ModalProps {
  onCancel: () => void;
  data?: {
    installmentIds: number[];
    totalValue: number;
  };
  onCancelInSubmit: () => void;
}

const GenerateGroupingModal: React.FC<Props> = ({
  onCancel,
  open,
  data,
  onCancelInSubmit,
}) => {
  const [form] = Form.useForm<AccountPayableGenerateGroupingFormValues>();
  const firstDueDate = Form.useWatch('installmentsFirstDueDate', form);
  const daysInterval = Form.useWatch('intervalDays', form);
  const installmentQuantity = Form.useWatch('quantityInstallments', form);
  const [installments, setInstallments] = useState<
    InstallmentFromGroupingsList[]
  >([]);

  const generateGrouping = useAccountPayableGenerateGrouping();

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
        installmentsFirstDueDate: moment(),
        intervalDays: 30,
        quantityInstallments: 1,
      });
    }
  }, [data]);

  useEffect(() => {
    if (
      daysInterval &&
      firstDueDate &&
      installmentQuantity &&
      data?.totalValue
    ) {
      const installmentValue = MathRound(
        data.totalValue / installmentQuantity,
        2,
      );

      setInstallments(
        new Array(installmentQuantity)
          .fill(0)
          .reduce<InstallmentFromGroupingsList[]>((acc, _, i) => {
            const dueDate =
              i === 0
                ? firstDueDate?.clone()
                : acc.at(-1)?.dueDate.clone().add(daysInterval, 'days');

            let installmentValueItem: number;

            if (i === installmentQuantity - 1) {
              installmentValueItem = MathRound(
                data.totalValue -
                  installmentValue * installmentQuantity +
                  installmentValue,
                2,
              );
            } else {
              installmentValueItem = installmentValue;
            }

            return [
              ...acc,
              {
                description: `${i + 1}ª Parcela`,
                dueDate: dueDate ?? moment(),
                installmentValue: installmentValueItem,
              },
            ];
          }, [] as InstallmentFromGroupingsList[]),
      );
    }
  }, [data, firstDueDate, daysInterval, installmentQuantity]);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleCancelInSubmit = () => {
    form.resetFields();
    onCancelInSubmit();
  };

  async function handleSubmit() {
    try {
      const values = await form.validateFields();

      if (data) {
        if (installments.every((installment) => !installment.documentTypeId)) {
          return notification.error({
            message: 'Informe o(s) tipo(s) de documento',
          });
        } else if (
          installments.every((installment) => !installment.bankAccountId)
        ) {
          return notification.error({
            message: 'Informe a(s) conta(s) bancária(s)',
          });
        }

        await generateGrouping.mutateAsync({
          ...values,
          installmentsFirstDueDate:
            values.installmentsFirstDueDate.format(DateTimeFormat),
          accountIds: data?.installmentIds,
          installmentFromGroupings: installments.map((item) => ({
            ...item,
            dueDate: item.dueDate.format(DateTimeFormat),
          })),
        });
        handleCancelInSubmit();
      }
    } catch {}
  }

  return (
    <Modal
      width={1400}
      open={open}
      onCancel={handleCancel}
      title="Geração do agrupamento"
      footer={[
        <Space>
          <Button type="primary" ghost onClick={handleCancel}>
            Cancelar
          </Button>

          <SaveButton
            type="primary"
            onClick={handleSubmit}
            loading={generateGrouping.isLoading}
          >
            Gerar Agrupamento
          </SaveButton>
        </Space>,
      ]}
      destroyOnClose
    >
      <GenerateGroupingForm form={form} />

      <GenerateGroupingInstallmentList
        installments={installments}
        setInstallments={setInstallments}
      />
    </Modal>
  );
};

export default GenerateGroupingModal;
